import type { ReportResponsePayload, ReportRow } from '../models/report.types'

export type MultiMetricPeriod = 'current' | 'before'

export interface MultiMetricSeries {
  platform: string
  metric: string
  current: Record<string, number>
  before: Record<string, number>
  current_total: number
  before_total: number
  current_count: number
  before_count: number
}

export type MultiMetricSeriesMap = Record<string, MultiMetricSeries>

export interface AggregateMultiMetricSeriesInput {
  rows: readonly Readonly<ReportRow>[]
  metrics: readonly string[]
  dateKeyOf: (row: Readonly<ReportRow>) => string
  platformOf?: (row: Readonly<ReportRow>) => string
  metricFieldOf?: (field: string) => string
  periodOf?: (row: Readonly<ReportRow>, dateKey: string) => MultiMetricPeriod
  /**
   * The row field a metric is read from in a period. A comparison row carries its baseline beside
   * the current value as `prev_<metric>` (ORP-24984); without this hook the metric field is read.
   */
  valueFieldOf?: (row: Readonly<ReportRow>, metric: string, period: MultiMetricPeriod) => string
  responseTotals?: Pick<ReportResponsePayload, 'total' | 'total_period'> | null
  calcRoasFirstMetric?: string
}

function createSeries(platform: string, metric: string): MultiMetricSeries {
  return {
    platform,
    metric,
    current: {},
    before: {},
    current_total: 0,
    before_total: 0,
    current_count: 0,
    before_count: 0,
  }
}

function numeric(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function optionalNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function add(series: MultiMetricSeries, period: MultiMetricPeriod, date: string, value: number) {
  series[period][date] = (series[period][date] ?? 0) + value
  series[`${period}_total`] += value
  series[`${period}_count`] += 1
}

/**
 * Module-08 authority for the legacy overview-chart response shape.
 *
 * Every aggregate starts with numeric totals/counts. The v3 implementation omitted those fields
 * and immediately used `+=`, producing `NaN` whenever the controller did not send a totals bag.
 */
export function aggregateMultiMetricSeries(
  input: AggregateMultiMetricSeriesInput,
): MultiMetricSeriesMap | null {
  if (input.rows.length === 0 || input.metrics.length === 0) return null
  const output: MultiMetricSeriesMap = {}
  const sourceByMetric = new Map<string, string>()

  for (const row of input.rows) {
    const platform = (input.platformOf?.(row) ?? String(row.platform ?? 'all')).toLowerCase()
    const dateKey = input.dateKeyOf(row)
    const period = input.periodOf?.(row, dateKey) ?? (row.is_previous ? 'before' : 'current')

    for (const sourceMetric of input.metrics) {
      const metric = input.metricFieldOf?.(sourceMetric) ?? sourceMetric
      sourceByMetric.set(metric, sourceMetric)
      const value = numeric(row[input.valueFieldOf?.(row, sourceMetric, period) ?? sourceMetric])
      const series = (output[`${platform}_${metric}`] ??= createSeries(platform, metric))
      add(series, period, dateKey, value)

      if (platform !== 'all') {
        const allSeries = (output[`all_${metric}`] ??= createSeries('all', metric))
        add(allSeries, period, dateKey, value)
      }
    }
  }

  for (const [metric, sourceMetric] of sourceByMetric) {
    const series = output[`all_${metric}`]
    if (!series) continue
    const current =
      optionalNumeric(input.responseTotals?.total_period?.[sourceMetric]) ??
      optionalNumeric(input.responseTotals?.total?.[sourceMetric])
    const before =
      optionalNumeric(input.responseTotals?.total_period?.[`prev_${sourceMetric}`]) ??
      optionalNumeric(input.responseTotals?.total?.[`prev_${sourceMetric}`])
    if (current !== null) series.current_total = current
    if (before !== null) series.before_total = before
  }

  if (input.calcRoasFirstMetric) {
    applyCalculatedRoas(output, input)
  }
  return output
}

/** Derive ratios after bucket aggregation and response-total overrides, never sum row ROAS. */
function applyCalculatedRoas(output: MultiMetricSeriesMap, input: AggregateMultiMetricSeriesInput) {
  const source = input.calcRoasFirstMetric!
  const numeratorField = input.metricFieldOf?.(source) ?? source
  const costField = input.metricFieldOf?.('cost') ?? 'cost'
  for (const series of Object.values(output)) {
    if (series.metric !== 'roas') continue
    const numerator = output[`${series.platform}_${numeratorField}`]
    const cost = output[`${series.platform}_${costField}`]
    if (!numerator || !cost) continue
    for (const period of ['current', 'before'] as const) {
      for (const date of Object.keys(series[period])) {
        series[period][date] = roasRatio(numerator[period][date], cost[period][date])
      }
      series[`${period}_total`] = roasRatio(numerator[`${period}_total`], cost[`${period}_total`])
    }
  }
}

function roasRatio(value: number | undefined, cost: number | undefined): number {
  return cost ? (value ?? 0) / cost : 0
}
